import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import SpaCommandsTable from "../../SpaReport/components/SpaCommandsTable";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import RootState from "../../../redux/states/root.state";
import AutoRefreshControl from "../../../components/common/AutoRefresh/AutoRefreshControl";
import { FilterLineIcon, TerminalLineIcon } from "../../../components/common/Icons/Icons";

// URL params the filter panel owns (counted on the Filters button)
const filterParams = ['title', 'guid'];

interface ContainerProps {
  onRefresh: () => void;
}

const SpaCommandsContainer: React.FC<ContainerProps> = ({ onRefresh }) => {
  const navigate = useNavigate();
  const {canAccess:canViewApiCommands} = usePermissionCheck(['view_api_commands']);
  const {loading} = useSelector((state: RootState) => state.commands);
  const [searchParams, setSearchParams] = useSearchParams();

  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);

  //Filters
  const [title, setTitle] = useState('');
  const [guid, setGuid] = useState('');

  useEffect(() => {
    setTitle(searchParams.get('title') ?? '');
    setGuid(searchParams.get('guid') ?? '');
  }, [searchParams]);

  const handleFilter = () => {
    searchParams.delete('page'); //reset page
    title === "" ? searchParams.delete('title') : searchParams.set('title', title);
    guid === "" ? searchParams.delete('guid') : searchParams.set('guid', guid);
    setSearchParams(searchParams);
  };

  const handleClear = () => {
    searchParams.delete('page'); //reset page
    setTitle("");
    searchParams.delete('title');
    setGuid("");
    searchParams.delete('guid');
    setSearchParams(searchParams);
  };

  const activeFilters = filterParams.filter((param) => searchParams.has(param)).length;

  const toolbar = (
    <>
      <button
        type="button"
        className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
        onClick={() => setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        aria-controls="lp-filters"
      >
        <FilterLineIcon/> Filters
        {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
      </button>

      {canViewApiCommands &&
        <button type="button" className="btn btn-sm dt-tool-btn lp-primary-action" onClick={() => navigate('/api-commands')}>
          <TerminalLineIcon/> View All Commands
        </button>}

      <AutoRefreshControl onRefresh={onRefresh} busy={loading} variant="toolbar"/>
    </>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group">
              <TextField
                id="title-search"
                label="Title"
                type="search"
                fullWidth={true}
                value={title ?? ""}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => (e.code === "Enter" ? handleFilter() : null)}
                size="small"
                autoComplete="spa-command-title"
              />

              <TextField
                id="guid-search"
                label="GUID"
                type="search"
                fullWidth={true}
                value={guid ?? ""}
                onChange={(e) => setGuid(e.target.value)}
                onKeyDown={(e) => (e.code === "Enter" ? handleFilter() : null)}
                size="small"
                autoComplete="spa-command-guid"
              />
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleFilter}>Apply Filters</button>
          </div>
        </aside>
      )}

      <div className="dt">
        <div className="dt-toolbar">
          <div className="dt-toolbar-start">{toolbar}</div>
        </div>
        <SpaCommandsTable/>
      </div>
    </div>
  );
};

export default SpaCommandsContainer;
